import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  AlertTriangle, BellRing, CheckCircle2, Copy, Eye, EyeOff, FileText, HelpCircle, Pencil, Plus, Send, Sparkles, Trash2,
  UploadCloud, User, X,
} from 'lucide-react';
import { apiService } from '../../services/api';
import LineAllocations, { ProjectTotals } from './LineAllocations';
import { fileHref, fullDate, money, Pill, ReceiptLinks, Spinner } from './ui';

// « À traiter » : les factures déposées. Déposées avec « Envoyer » : Nexus les lit, les répartit d'après le message
// et les enregistre seul quand tout est clair ; sinon la carte pose une question (réponse en une phrase). Déposées
// sans envoi : la répartition proposée se valide d'un clic, comme avant.

const PENNYLANE = {
  uploaded: { tone: 'green', label: 'Envoyée à Pennylane' },
  excluded_amazon: { tone: 'gray', label: 'Amazon : non envoyée à Pennylane' },
  held_duplicate: { tone: 'amber', label: 'Doublon probable : pas encore envoyée' },
  failed: { tone: 'red', label: 'Pennylane : échec, nouvel essai automatique' },
};

// Doublon probable : pourquoi on le pense, qui l'a déjà saisie, et quoi faire (écarter, ou garder si c'est une autre).
const DUPLICATE_REASONS = {
  same_file: 'le même fichier PDF',
  same_number: 'le même numéro de facture',
  same_date_amount: 'le même fournisseur, la même date et le même montant',
};

// Le double peut aussi être une autre facture déposée, pas encore validée (facture et reçu Stripe déposés ensemble).
const DuplicateNotice = ({ duplicate, currency, busy, onOpen, onDiscard, onKeep }) => {
  const deposited = Boolean(duplicate.draft_id && duplicate.pending);
  return (
  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
    <p className="flex items-start gap-2 font-medium">
      <Copy className="mt-0.5 h-4 w-4 shrink-0" />
      {deposited ? 'Cette facture semble déjà déposée.' : 'Cette facture semble déjà enregistrée.'}
    </p>
    <p className="mt-1 pl-6 text-xs leading-relaxed text-amber-800">
      {deposited ? `Une autre facture déposée${duplicate.original_name ? ` (${duplicate.original_name})` : ''} a` : 'Une dépense existante a'}
      {' '}{DUPLICATE_REASONS[duplicate.reason] || 'les mêmes informations'}
      {duplicate.invoice_number ? ` (n° ${duplicate.invoice_number})` : ''} : {duplicate.vendor}, {fullDate(duplicate.date)},{' '}
      <span className="font-semibold">{money(duplicate.amount, currency)} HT</span>
      {duplicate.entered_by ? `, ${deposited ? 'déposée' : 'saisie'} par ${duplicate.entered_by}` : ''}
      {duplicate.entered_at ? ` le ${fullDate(duplicate.entered_at)}` : ''}.
      {' '}Elle n'a pas été envoyée à Pennylane une seconde fois.
    </p>
    <div className="mt-2 flex flex-wrap gap-2 pl-6">
      <button type="button" onClick={onDiscard} disabled={busy === 'discard'}
        className="rounded-md bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
        C'est un doublon : retirer
      </button>
      {onOpen && (
        <button type="button" onClick={onOpen}
          className="rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100">
          {deposited ? "Voir l'autre facture" : "Comparer avec l'existante"}
        </button>
      )}
      <button type="button" onClick={onKeep} disabled={busy === 'duplicate'}
        className="rounded-md px-2.5 py-1 text-xs font-medium text-amber-800 underline-offset-2 hover:underline disabled:opacity-50">
        C'est une autre facture
      </button>
    </div>
  </div>
  );
};

// Où regarder le double : la dépense enregistrée, ou l'autre facture de la file (seulement les siennes).
const openDuplicate = (duplicate, onOpenInvoice) => {
  if (duplicate.expense_id) return () => onOpenInvoice(duplicate.expense_id);
  if (!duplicate.mine) return null;
  return () => document.getElementById(`draft-${duplicate.draft_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const status = (draft) => {
  if (draft.attention) return { tone: 'amber', label: 'À toi de voir' };
  if (draft.auto && (draft.status === 'analyzing' || draft.allocating)) return { tone: 'violet', label: 'Nexus s’en occupe', spin: true };
  if (draft.status === 'analyzing') return { tone: 'blue', label: 'Lecture…', spin: true };
  if (draft.status === 'failed') return { tone: 'red', label: 'Illisible' };
  if (draft.allocating) return { tone: 'violet', label: 'Répartition…', spin: true };
  if (draft.proposal?.complete) return { tone: 'green', label: 'Prête' };
  return { tone: 'amber', label: 'À compléter' };
};

// Correction de la lecture (ou saisie quand la facture est illisible) : en-tête et lignes.
const InvoiceEditor = ({ invoice, onCancel, onSave, saving }) => {
  const [head, setHead] = useState({
    vendor: invoice?.vendor || '', number: invoice?.number || '', date: invoice?.date || '',
    total_ht: invoice?.total_ht ?? '', tax: invoice?.tax ?? '', currency: invoice?.currency || '€',
  });
  const [lines, setLines] = useState((invoice?.lines || []).filter((l) => l.designation !== 'Écart avec le total de la facture')
    .map((l) => ({ kind: l.kind, ref: l.mfr_ref || l.ref || '', designation: l.designation, quantity: String(l.quantity), total: String(l.total) })));
  const set = (key) => (e) => setHead((h) => ({ ...h, [key]: e.target.value }));
  const setLine = (index, patch) => setLines((all) => all.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const num = (v) => (v === '' || v === null ? null : Number(String(v).replace(/\s/g, '').replace(',', '.')));
  const input = 'rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-blue-400 focus:outline-none';
  const submit = () => onSave({
    vendor: head.vendor, number: head.number, date: head.date, currency: head.currency,
    total_ht: num(head.total_ht), tax: num(head.tax),
    lines: lines.filter((l) => l.designation.trim()).map((l) => ({
      kind: l.kind, ref: l.ref || null, designation: l.designation, quantity: num(l.quantity) || 1, total: num(l.total) || 0,
    })),
  });
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
        <input className={`${input} col-span-2`} placeholder="Fournisseur" value={head.vendor} onChange={set('vendor')} />
        <input className={input} placeholder="N° de facture" value={head.number} onChange={set('number')} />
        <input className={input} type="date" value={head.date} onChange={set('date')} />
        <input className={`${input} text-right`} placeholder="Total HT" inputMode="decimal" value={head.total_ht} onChange={set('total_ht')} />
        <div className="flex gap-1">
          <input className={`${input} min-w-0 flex-1 text-right`} placeholder="TVA" inputMode="decimal" value={head.tax} onChange={set('tax')} />
          <select className={input} value={head.currency} onChange={set('currency')}>
            <option value="€">€</option><option value="$">$</option>
          </select>
        </div>
      </div>
      <div className="space-y-1">
        {lines.map((line, index) => (
          <div key={index} className="flex items-center gap-1.5">
            <select className={`${input} w-24`} value={line.kind} onChange={(e) => setLine(index, { kind: e.target.value })}>
              <option value="article">Article</option><option value="frais">Frais</option><option value="remise">Remise</option>
            </select>
            <input className={`${input} w-28 font-mono text-xs`} placeholder="Réf." value={line.ref} onChange={(e) => setLine(index, { ref: e.target.value })} />
            <input className={`${input} min-w-0 flex-1`} placeholder="Désignation" value={line.designation} onChange={(e) => setLine(index, { designation: e.target.value })} />
            <input className={`${input} w-16 text-right`} inputMode="decimal" value={line.quantity} onChange={(e) => setLine(index, { quantity: e.target.value })} aria-label="Quantité" />
            <input className={`${input} w-24 text-right`} inputMode="decimal" placeholder="Total HT" value={line.total} onChange={(e) => setLine(index, { total: e.target.value })} />
            <button type="button" onClick={() => setLines((all) => all.filter((_, i) => i !== index))} className="p-1 text-gray-400 hover:text-red-600" aria-label="Retirer la ligne">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => setLines((all) => [...all, { kind: 'article', ref: '', designation: '', quantity: '1', total: '' }])}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline">
          <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
        </button>
      </div>
      <p className="text-xs text-gray-500">Si les lignes ne font pas le total HT, l'écart est ventilé comme les frais de port.</p>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-white">Annuler</button>
        <button type="button" onClick={submit} disabled={saving || !lines.some((l) => l.designation.trim())}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          Enregistrer la lecture
        </button>
      </div>
    </div>
  );
};

// Ce que Nexus demande pour finir une facture déposée avec « Envoyer » ; on répond en une phrase.
const AttentionBox = ({ attention, busy, onReply }) => {
  const [text, setText] = useState('');
  const canReply = attention.kind === 'repartition' || attention.kind === 'lecture';
  const send = () => { if (text.trim()) onReply(text.trim()).then((ok) => ok && setText('')); };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
      <p className="flex items-start gap-2 font-medium"><BellRing className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />{attention.question}</p>
      {canReply && (
        <div className="mt-2 flex gap-2 pl-6">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder={attention.kind === 'lecture' ? 'ex. « le total HT est 1 234,50 € »' : 'ex. « le fer à souder va sur le labo »'}
            className="min-w-0 flex-1 rounded-md border border-amber-200 bg-white px-2.5 py-1.5 text-sm focus:border-amber-400 focus:outline-none" />
          <button type="button" onClick={send} disabled={busy === 'reply' || !text.trim()}
            className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-40">
            {busy === 'reply' ? <Spinner className="h-4 w-4 text-white" /> : <Send className="h-4 w-4" />} Répondre
          </button>
        </div>
      )}
      {attention.kind === 'montant' && <p className="mt-1 pl-6 text-xs text-amber-800">Vérifie la répartition ci-dessous, puis « Valider ».</p>}
    </div>
  );
};

// Facture prise en charge par Nexus (dépôt « Envoyer ») : une ligne, sans rien à faire.
const HandledRow = ({ draft }) => {
  const state = status(draft);
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-2 text-sm text-gray-600">
      <FileText className="h-4 w-4 shrink-0 text-gray-400" />
      <span className="min-w-0 flex-1 truncate">{draft.invoice?.vendor || draft.original_name}
        {draft.instruction && <span className="text-gray-400"> · « {draft.instruction} »</span>}</span>
      <Pill tone={state.tone}>{state.spin && <Spinner className="h-3 w-3" />}{state.label}</Pill>
    </div>
  );
};

const DraftCard = ({ draft, projects, people, onChanged, onRemoved, onValidated, onOpenInvoice }) => {
  const [instruction, setInstruction] = useState(draft.instruction || '');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [showPdf, setShowPdf] = useState(false);
  const [editing, setEditing] = useState(false);
  const typing = useRef(false);
  const invoice = draft.invoice;
  const proposal = draft.proposal;
  const state = status(draft);
  const currency = invoice?.currency || '€';
  const pennylane = PENNYLANE[draft.pennylane_status];
  // La question posée dans l'encadré « À toi de voir » n'est pas répétée dans la liste.
  const otherQuestions = (proposal?.questions || []).filter((q) => q !== draft.attention?.question);

  useEffect(() => {
    if (!typing.current) setInstruction(draft.instruction || '');
  }, [draft.instruction]);

  const run = async (kind, action) => {
    setBusy(kind);
    setError(null);
    try {
      return await action();
    } catch (err) {
      setError(err.message || 'Erreur');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const sendInstruction = () => run('instruction', async () => {
    typing.current = false;
    onChanged(await apiService.patch(`/expense-drafts/${draft.id}`, { instruction }));
  });
  const saveAllocations = (allocations) => run('allocations', async () => {
    onChanged(await apiService.patch(`/expense-drafts/${draft.id}`, { allocations }));
  });
  const saveInvoice = (edits) => run('invoice', async () => {
    onChanged(await apiService.patch(`/expense-drafts/${draft.id}`, { invoice: edits }));
    setEditing(false);
  });
  const setBuyer = (employeeId) => run('buyer', async () => {
    onChanged(await apiService.patch(`/expense-drafts/${draft.id}`, { employee_id: Number(employeeId) }));
  });
  const validate = () => run('validate', async () => {
    const result = await apiService.post(`/expense-drafts/${draft.id}/validate`, {});
    onValidated(draft.id, result.expense_id);
  });
  const reply = (text) => run('reply', async () => {
    const updated = await apiService.post(`/expense-drafts/${draft.id}/reply`, { text });
    if (updated.status === 'validated' || updated.expense_id) onValidated(draft.id, updated.expense_id);
    else onChanged(updated);
    return true;
  });
  const notDuplicate = () => run('duplicate', async () => {
    onChanged(await apiService.patch(`/expense-drafts/${draft.id}`, { not_duplicate: true }));
  });
  const discard = (confirmed = false) => {
    const sent = draft.pennylane_status === 'uploaded' ? ' Elle a déjà été envoyée à Pennylane.' : '';
    if (!confirmed && !window.confirm(`Retirer cette facture ?${sent}`)) return;
    run('discard', async () => {
      await apiService.delete(`/expense-drafts/${draft.id}`);
      onRemoved(draft.id);
    });
  };

  return (
    <article id={`draft-${draft.id}`} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-100 px-4 py-2.5">
        <FileText className="h-4 w-4 shrink-0 text-gray-400" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{invoice?.vendor || draft.original_name}</p>
          <p className="truncate text-xs text-gray-500">
            {invoice?.date ? fullDate(invoice.date) : draft.status === 'analyzing' ? 'Lecture de la facture…' : draft.original_name}
            {invoice?.number && <span> · n° {invoice.number}</span>}
            {invoice?.ordered_by && <span> · commandée par {invoice.ordered_by}</span>}
          </p>
        </div>
        {invoice && (
          <div className="text-right">
            <p className="text-sm font-semibold tabular-nums text-gray-900">{money(invoice.total_ht, currency)} <span className="text-xs font-normal text-gray-500">HT</span></p>
            {invoice.total_ttc != null && <p className="text-[11px] tabular-nums text-gray-500">{money(invoice.total_ttc, currency)} TTC</p>}
          </div>
        )}
        <Pill tone={state.tone}>{state.spin && <Spinner className="h-3 w-3" />}{state.label}</Pill>
        <div className="flex items-center gap-0.5">
          <ReceiptLinks receipts={draft.receipts} />
          <button type="button" onClick={() => setShowPdf((v) => !v)} title={showPdf ? 'Masquer la facture' : 'Voir la facture'}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            {showPdf ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
          {invoice && (
            <button type="button" onClick={() => setEditing((v) => !v)} title="Corriger la lecture"
              className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
              <Pencil className="h-4 w-4" />
            </button>
          )}
          <button type="button" onClick={() => discard()} title="Retirer" className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className={showPdf ? 'grid gap-0 lg:grid-cols-2' : ''}>
        {showPdf && (
          <iframe title={`Facture ${draft.original_name}`} src={fileHref(draft.file_url)}
            className="h-[28rem] w-full border-b border-gray-100 bg-gray-50 lg:h-full lg:min-h-[28rem] lg:border-b-0 lg:border-r" />
        )}
        <div className="space-y-3 px-4 py-3">
          {/* Consigne : utilisable pendant la lecture */}
          <div className="flex items-start gap-2">
            <Sparkles className="mt-2 h-4 w-4 shrink-0 text-violet-500" />
            <textarea
              rows={1}
              value={instruction}
              onChange={(e) => { typing.current = true; setInstruction(e.target.value); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendInstruction(); } }}
              placeholder="Où va cette facture ? ex. « 15 LX pour Olista 70, 15 pour Olista 30, le fer sur le labo »"
              className="min-h-[2.25rem] flex-1 resize-y rounded-lg border border-gray-200 px-3 py-1.5 text-sm placeholder:text-gray-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100"
            />
            <button type="button" onClick={sendInstruction} disabled={busy === 'instruction' || (instruction || '') === (draft.instruction || '')}
              className="mt-0.5 inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-40">
              {busy === 'instruction' ? <Spinner className="h-4 w-4 text-white" /> : <Send className="h-4 w-4" />}
              <span className="hidden sm:inline">Répartir</span>
            </button>
          </div>
          {draft.status === 'analyzing' && draft.instruction && (
            <p className="text-xs text-gray-500">Consigne notée : elle sera appliquée dès que la facture sera lue.</p>
          )}

          {draft.attention && draft.attention.kind !== 'doublon' && (
            <AttentionBox attention={draft.attention} busy={busy} onReply={reply} />
          )}
          {draft.duplicate && (
            <DuplicateNotice duplicate={draft.duplicate} currency={currency} busy={busy}
              onOpen={openDuplicate(draft.duplicate, onOpenInvoice)}
              onDiscard={() => discard(true)} onKeep={notDuplicate} />
          )}
          {(draft.warnings?.length > 0 || otherQuestions.length > 0) && (
            <ul className="space-y-1">
              {otherQuestions.map((q) => (
                <li key={q} className="flex gap-2 rounded-md bg-violet-50 px-2.5 py-1.5 text-xs text-violet-900"><HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{q}</li>
              ))}
              {(draft.warnings || []).map((w) => (
                <li key={w} className="flex gap-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{w}</li>
              ))}
            </ul>
          )}
          {draft.status === 'failed' && !editing && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
              {draft.error || 'La facture n’a pas pu être lue.'}
              <button type="button" onClick={() => setEditing(true)} className="font-medium underline">Saisir les lignes</button>
            </div>
          )}

          {editing && (
            <InvoiceEditor invoice={invoice} saving={busy === 'invoice'} onCancel={() => setEditing(false)} onSave={saveInvoice} />
          )}

          {invoice && !editing && (
            <LineAllocations
              lines={invoice.lines}
              allocations={proposal?.allocations || []}
              unallocated={proposal?.unallocated || Object.fromEntries(invoice.lines.filter((l) => l.kind === 'article').map((l) => [String(l.position), l.quantity]))}
              currency={currency}
              projects={projects}
              editable
              saving={busy === 'allocations'}
              onChange={saveAllocations}
            />
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      </div>

      {invoice && (
        <footer className="flex flex-wrap items-center gap-3 border-t border-gray-100 bg-gray-50/60 px-4 py-2.5">
          <div className="min-w-0 flex-1">
            {proposal?.projects?.length ? <ProjectTotals projects={proposal.projects} currency={currency} />
              : <span className="text-xs text-gray-400">Aucun projet choisi pour l'instant.</span>}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-500" title="Personne pour qui la dépense est enregistrée">
            <User className="h-3.5 w-3.5" />
            {people.length ? (
              <select value={draft.employee_id || ''} onChange={(e) => setBuyer(e.target.value)}
                className="rounded-md border border-gray-200 bg-white px-1.5 py-1 text-xs text-gray-700">
                {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            ) : <span className="text-gray-700">{draft.employee_name || '—'}</span>}
          </label>
          {pennylane && <Pill tone={pennylane.tone}>{pennylane.label}</Pill>}
          <button type="button" onClick={validate} disabled={!proposal?.complete || busy === 'validate' || draft.allocating}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40">
            {busy === 'validate' ? <Spinner className="h-4 w-4 text-white" /> : <CheckCircle2 className="h-4 w-4" />} Valider
          </button>
        </footer>
      )}
    </article>
  );
};

// Plusieurs factures sans consigne (dépôt groupé) : un seul message pour toutes, puis Nexus termine seul.
const BatchBar = ({ drafts, onSent }) => {
  const [left, setLeft] = useState([]); // factures retirées du lot
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const chosen = drafts.filter((d) => !left.includes(d.id));
  const send = async () => {
    if (!text.trim() || !chosen.length) return;
    setBusy(true);
    setError(null);
    try {
      onSent(await apiService.post('/expense-drafts/batch-instruction', { draft_ids: chosen.map((d) => d.id), text: text.trim() }));
      setText('');
      setLeft([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2 rounded-xl border border-violet-200 bg-violet-50/50 p-3">
      <p className="text-sm font-medium text-gray-800">Un seul message pour ces {chosen.length} factures ?</p>
      <div className="flex flex-wrap gap-1.5">
        {drafts.map((d) => {
          const out = left.includes(d.id);
          return (
            <button key={d.id} type="button" onClick={() => setLeft((ids) => (out ? ids.filter((i) => i !== d.id) : [...ids, d.id]))}
              title={out ? 'Remettre dans le lot' : 'Retirer du lot'}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs ring-1 ${out ? 'bg-gray-50 text-gray-400 line-through ring-gray-200' : 'bg-white text-gray-700 ring-violet-200'}`}>
              <FileText className="h-3 w-3 text-gray-400" />{d.invoice?.vendor || d.original_name}
            </button>
          );
        })}
      </div>
      <div className="flex items-start gap-2">
        <Sparkles className="mt-2 h-4 w-4 shrink-0 text-violet-500" />
        <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Où vont ces achats ? ex. « tout pour le projet Mantos »"
          className="min-h-[2.5rem] flex-1 resize-y rounded-lg border border-violet-200 bg-white px-3 py-1.5 text-sm placeholder:text-gray-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100" />
        <button type="button" onClick={send} disabled={busy || !text.trim() || !chosen.length}
          className="mt-0.5 inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
          {busy ? <Spinner className="h-4 w-4 text-white" /> : <Send className="h-4 w-4" />} Envoyer
        </button>
      </div>
      {error && <p className="pl-6 text-xs text-red-600">{error}</p>}
      <p className="pl-6 text-xs text-gray-500">Tu peux partir : Nexus répartit et enregistre. S'il a un doute, la facture t'attendra ici avec sa question.</p>
    </div>
  );
};

const Inbox = forwardRef(({ projects, people, isAdmin = false, employeeId = null, onValidated, onOpenInvoice }, ref) => {
  const [all, setAll] = useState([]);
  const [showOthers, setShowOthers] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState(null);
  const [validatingAll, setValidatingAll] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [message, setMessage] = useState('');
  const input = useRef(null);

  // Un admin voit aussi les factures déposées par les autres (repliées par défaut).
  const handled = useRef(new Set()); // factures confiées à Nexus : quand elles quittent la liste, elles sont enregistrées
  const load = useCallback(() => apiService.get(`/expense-drafts${isAdmin ? '?everyone=true' : ''}`)
    .then((rows) => {
      const list = rows || [];
      const present = new Set(list.map((d) => d.id));
      const recorded = [...handled.current].filter((id) => !present.has(id));
      handled.current = new Set(list.filter((d) => d.auto).map((d) => d.id));
      setAll(list);
      if (recorded.length) onValidated();
    }).catch((err) => setError(err.message)), [isAdmin, onValidated]);
  const others = all.filter((d) => employeeId && d.created_by_employee !== employeeId);
  const drafts = showOthers ? all : all.filter((d) => !employeeId || d.created_by_employee === employeeId);

  useEffect(() => { load(); }, [load]);

  // Rafraîchissement : rapide tant qu'une facture est en lecture ou en répartition.
  const pending = all.some((d) => d.status === 'analyzing' || d.allocating);
  useEffect(() => {
    const timer = setTimeout(load, pending ? 2000 : 30000);
    return () => clearTimeout(timer);
  }, [all, pending, load]);

  // Fichiers choisis ou glissés : ils attendent le message, puis « Envoyer » (Nexus s'occupe du reste).
  const upload = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.(pdf|png|jpe?g)$/i.test(f.name));
    if (!files.length) {
      setError('Formats acceptés : PDF, PNG, JPEG.');
      return;
    }
    setError(null);
    setPendingFiles((current) => [...current, ...files]);
  }, []);

  const send = useCallback(async () => {
    const files = pendingFiles;
    if (!files.length) return;
    setError(null);
    setUploading(files.length);
    try {
      const data = new FormData();
      files.forEach((file) => data.append('files', file));
      if (message.trim()) data.append('instruction', message.trim());
      data.append('auto', 'true');
      const created = await apiService.uploadFile('/expense-drafts', data);
      setPendingFiles([]);
      setMessage('');
      setAll((current) => [...(created || []).filter((d) => !current.some((c) => c.id === d.id)), ...current]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(0);
    }
  }, [pendingFiles, message]);

  useImperativeHandle(ref, () => ({ upload }), [upload]);

  const replace = (draft) => setAll((current) => current.map((d) => (d.id === draft.id ? draft : d)));
  const remove = (id) => {
    setAll((current) => current.filter((d) => d.id !== id));
    if (all.some((d) => d.duplicate?.draft_id === id)) load(); // son double, retenu, part maintenant à Pennylane
  };
  const validated = (id, expenseId) => { remove(id); onValidated(expenseId); };
  const ready = drafts.filter((d) => d.status === 'ready' && d.proposal?.complete && !d.allocating);
  const unexplained = all.filter((d) => (!employeeId || d.created_by_employee === employeeId) && !d.auto
    && !(d.instruction || '').trim() && ['analyzing', 'ready', 'failed'].includes(d.status) && !d.proposal?.manual);
  const batchSent = (rows) => {
    (rows || []).forEach((d) => handled.current.add(d.id));
    setAll((current) => current.map((d) => (rows || []).find((r) => r.id === d.id) || d));
  };

  const validateAll = async () => {
    setValidatingAll(true);
    for (const draft of ready) {
      try {
        const result = await apiService.post(`/expense-drafts/${draft.id}/validate`, {});
        validated(draft.id, result.expense_id);
      } catch (err) {
        setError(`${draft.invoice?.vendor || draft.original_name} : ${err.message}`);
      }
    }
    setValidatingAll(false);
  };

  return (
    <section className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => input.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') input.current?.click(); }}
        className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed px-4 transition hover:border-blue-400 hover:bg-blue-50/40
          ${drafts.length ? 'border-gray-200 py-2.5' : 'border-gray-300 bg-white py-6'}`}
      >
        <UploadCloud className={`${drafts.length ? 'h-5 w-5' : 'h-8 w-8'} shrink-0 text-blue-500`} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-800">
            {uploading ? `Envoi de ${uploading} facture${uploading > 1 ? 's' : ''}…` : 'Glisse tes factures ici, ou clique pour les choisir'}
          </p>
          {!drafts.length && (
            <p className="text-xs text-gray-500">PDF ou photo, plusieurs à la fois. Écris en une phrase où vont les achats, puis « Envoyer » : Nexus s'occupe du reste.</p>
          )}
        </div>
        {uploading > 0 && <Spinner />}
        <input ref={input} type="file" multiple accept=".pdf,.png,.jpg,.jpeg" className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
      </div>
      {pendingFiles.length > 0 && (
        <div className="space-y-2 rounded-xl border border-violet-200 bg-violet-50/50 p-3">
          <div className="flex flex-wrap gap-1.5">
            {pendingFiles.map((file, index) => (
              <span key={`${file.name}-${index}`} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-0.5 text-xs text-gray-700 ring-1 ring-gray-200">
                <FileText className="h-3 w-3 text-gray-400" />{file.name}
                <button type="button" onClick={() => setPendingFiles((all) => all.filter((_, i) => i !== index))} aria-label={`Retirer ${file.name}`}
                  className="text-gray-400 hover:text-red-600"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex items-start gap-2">
            <Sparkles className="mt-2 h-4 w-4 shrink-0 text-violet-500" />
            <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Où vont ces achats ? ex. « 15 LX pour Olista 70, 15 pour Olista 30, le fer sur le labo »"
              className="min-h-[2.5rem] flex-1 resize-y rounded-lg border border-violet-200 bg-white px-3 py-1.5 text-sm placeholder:text-gray-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100" />
            <button type="button" onClick={send} disabled={uploading > 0}
              className="mt-0.5 inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
              {uploading ? <Spinner className="h-4 w-4 text-white" /> : <Send className="h-4 w-4" />} Envoyer
            </button>
          </div>
          <p className="pl-6 text-xs text-gray-500">Tu peux partir : Nexus lit, répartit et enregistre. S'il a un doute, la facture t'attendra ici avec sa question.</p>
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {unexplained.length > 1 && <BatchBar drafts={unexplained} onSent={batchSent} />}
      {(drafts.length > 0 || others.length > 0) && (
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            À traiter <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{drafts.length}</span>
            {others.length > 0 && (
              <button type="button" onClick={() => setShowOthers((v) => !v)} className="text-xs font-normal text-blue-700 hover:underline">
                {showOthers ? 'Masquer celles des autres' : `Voir les ${others.length} déposée${others.length > 1 ? 's' : ''} par d'autres`}
              </button>
            )}
          </h2>
          {ready.length > 1 && (
            <button type="button" onClick={validateAll} disabled={validatingAll}
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
              {validatingAll ? <Spinner className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />} Tout valider ({ready.length})
            </button>
          )}
        </div>
      )}
      {drafts.map((draft) => (draft.auto && !draft.attention && draft.status !== 'failed' ? (
        <HandledRow key={draft.id} draft={draft} />
      ) : (
        <DraftCard key={draft.id} draft={draft} projects={projects} people={people}
          onChanged={replace} onRemoved={remove} onValidated={validated} onOpenInvoice={onOpenInvoice} />
      )))}
    </section>
  );
});

export default Inbox;
